import _ from '@lodash';

const cloneValue = value => {
	if (_.isObject(value)) return JSON.parse(JSON.stringify(value));
	return value;
};

const parseAttributes = attributes => {
	if (_.isString(attributes)) {
		try {
			return JSON.parse(attributes);
		} catch (error) {
			return null;
		}
	}

	return attributes;
};

const attributeKey = attribute => `${attribute.entity || 'aspect'}|${attribute.io || 'INPUT'}|${attribute.name}`;

const flattenValue = (value, path = []) => {
	if (_.isPlainObject(value) && Object.keys(value).length > 0) {
		return Object.keys(value).reduce((result, valueKey) => [
			...result,
			...flattenValue(value[valueKey], [...path, valueKey])
		], []);
	}

	return [{
		path,
		value
	}];
};

const getDisplayValue = value => {
	if (_.isUndefined(value)) return '';
	if (_.isNull(value)) return 'null';
	if (_.isPlainObject(value) || _.isArray(value)) return JSON.stringify(value);
	return String(value);
};

const getExperimentAttributeLookup = experiment => {
	const attributes = experiment && _.isArray(experiment.attributes) ? experiment.attributes : [];

	return attributes.reduce((result, attribute) => ({
		...result,
		[attributeKey(attribute)]: attribute
	}), {});
};

export const getScenarioInputAttributes = experiment => {
	if (!experiment || !_.isArray(experiment.attributes)) return [];

	return experiment.attributes
		.filter(attribute => attribute.io === 'INPUT')
		.map(attribute => ({
			...attribute,
			entity: attribute.entity || 'aspect',
			value: cloneValue(attribute.value)
		}));
};

export const normalizeScenarioAttributes = attributes => {
	const parsedAttributes = parseAttributes(attributes);

	if (_.isArray(parsedAttributes)) {
		return parsedAttributes
			.filter(attribute => attribute && attribute.name)
			.map(attribute => ({
				...attribute,
				io: attribute.io || 'INPUT',
				entity: attribute.entity || 'aspect'
			}));
	}

	if (_.isObject(parsedAttributes)) {
		return Object.keys(parsedAttributes).map(name => ({
			name,
			io: 'INPUT',
			entity: 'aspect',
			value: parsedAttributes[name]
		}));
	}

	return [];
};

export const applyScenarioAttributesToExperiment = (experiment, scenarioAttributes) => {
	if (!experiment || !_.isArray(experiment.attributes)) return experiment;

	const normalizedAttributes = normalizeScenarioAttributes(scenarioAttributes);
	const scenarioValueByKey = normalizedAttributes.reduce((result, attribute) => ({
		...result,
		[attributeKey(attribute)]: attribute
	}), {});

	const scenarioValueByName = normalizedAttributes.reduce((result, attribute) => {
		if (result[attribute.name]) return result;
		return {
			...result,
			[attribute.name]: attribute
		};
	}, {});

	return {
		...experiment,
		validity: false,
		attributes: experiment.attributes.map(attribute => {
			if (attribute.io !== 'INPUT') return attribute;

			const lookupAttribute = {
				...attribute,
				entity: attribute.entity || 'aspect'
			};
			const scenarioAttribute = scenarioValueByKey[attributeKey(lookupAttribute)] || scenarioValueByName[attribute.name];

			if (!scenarioAttribute) return attribute;

			return {
				...attribute,
				value: _.isPlainObject(attribute.value) && _.isPlainObject(scenarioAttribute.value)
					? _.merge({}, cloneValue(attribute.value), cloneValue(scenarioAttribute.value))
					: cloneValue(scenarioAttribute.value)
			};
		})
	};
};

export const getScenarioChangedRows = (experiment, scenarioAttributes) => {
	const normalizedAttributes = normalizeScenarioAttributes(scenarioAttributes);
	const experimentAttributeByKey = getExperimentAttributeLookup(experiment);

	return normalizedAttributes.reduce((result, scenarioAttribute) => {
		const currentAttribute = experimentAttributeByKey[attributeKey(scenarioAttribute)];
		const valueRows = flattenValue(scenarioAttribute.value);

		return [
			...result,
			...valueRows.reduce((rowResult, valueRow) => {
				const currentValue = currentAttribute && valueRow.path.length > 0
					? _.get(currentAttribute.value, valueRow.path)
					: currentAttribute?.value;

				if (_.isEqual(currentValue, valueRow.value)) return rowResult;

				return [
					...rowResult,
					{
						key: `${attributeKey(scenarioAttribute)}|${valueRow.path.join('.') || 'value'}`,
						name: [scenarioAttribute.name, ...valueRow.path].join('.'),
						entity: scenarioAttribute.entity || 'aspect',
						type: scenarioAttribute.type || '',
						currentValue,
						scenarioValue: valueRow.value,
						currentValueText: getDisplayValue(currentValue),
						scenarioValueText: getDisplayValue(valueRow.value)
					}
				];
			}, [])
		];
	}, []);
};

export const getScenarioViewMessage = scenario => (
	scenario && scenario.name
		? `You are currently viewing ${scenario.name} scenario.`
		: null
);
